export const valueAttr = (element) => element.type === 'checkbox' ? 'checked' : 'value';

export const listInputName = (baseName, index) => `${baseName}__${index}`;

export const isBasicInput = (element) => ['input', 'select', 'textarea'].includes(element.nodeName.toLowerCase());

export const isListInput = (element) => ['list'].includes(element.nodeName.toLowerCase());

export const basicElementValue = (element) => {
  if (element.type === 'radio' && !element.checked) {
    return null;
  }

  return element[valueAttr(element)];
};

export const setElementValue = (element, value) => {
  if (isListInput(element) && !isBasicInput(element)) {
    element.setAttribute('data-value', JSON.stringify(value));
  } else if (!isBasicInput(element)) {
    element.setAttribute('data-value', value);
  } else if (element.type === 'checkbox') {
    element.checked = value;
  } else if (element.type === 'radio') {
    element.checked = element.value === value;
  } else {
    element.value = value;
  }
};

export const limitByName = (name) => (element) => !name || element.getAttribute('name') === name;


/**
 * Helper functions to work with react-hook-form.
 *
 * @param {Object} fields Form fields config object
 * @param {Object} formMethods return value from useForm() invocation
 */
export function formHelperFunctions(fields, formMethods) {
  const keys = Object.keys(fields);

  function throwIfNoName(name) {
    if (!fields[name]) {
      throw new Error(
        `Field "${name}" doesn't exist in the form schema.\nValid fields are: ${keys.join(
          ', ',
        )}`,
      );
    }
  }

  function errors(name) {
    return formMethods?.errors?.[name];
  }

  /**
   * Returns data to be used by the Form.Label.
   *
   * @param {String} name Field name
   */
  function label(name) {
    throwIfNoName(name);

    return {
      errors: errors(name) || undefined,
      htmlFor: fields[name].name,
      label: fields[name].label,
      required: fields[name]?.validationRules?.required,
    };
  }

  /**
   * Returns the validation rules used by a field.
   *
   * @param {String} name Field name
   */
  function rules(name) {
    throwIfNoName(name);

    return {
      ...fields[name].validationRules,
    };
  }

  /**
   * Returns data to be used by the Form.(Input|Textarea|Select).
   *
   * @param {String} name Field name
   * @param {Bool} isCheckbox Returns less information for checkbox usage
   */
  function field(name, isCheckbox = false) {
    throwIfNoName(name);

    if (isCheckbox) {
      return {
        name: fields[name].name,
        ref: formMethods?.register(rules(name)),
      };
    }

    return {
      control: formMethods?.control,
      errors: errors(name) || undefined,
      id: fields[name].name,
      name: fields[name].name,
      placeholder: fields[name].placeholder,
      ref: formMethods?.register(rules(name)),
    };
  }

  /**
   * Returns error data to be used by the Form.Error.
   *
   * @param {String} name Field name
   */
  function error(name) {
    throwIfNoName(name);

    const errorObject = errors(name);

    if (!errorObject) return {};

    return {
      label: fields[name].label,
      message: errorObject?.message,
      type: errorObject?.type,
    };
  }

  /**
   * Returns error data to be used by the Form.Error for Select.
   *
   * @param {String} name Field name
   */
  function errorSelect(name) {
    throwIfNoName(name);

    const errorObject = errors(name);

    if (!errorObject) return {};

    return {
      label: fields[name].label,
      message: errorObject?.value?.message,
      type: errorObject?.value?.type,
    };
  }

  return {
    error,
    errorSelect,
    field,
    label,
    rules,
  };
}

